<script lang="ts">
  import { registerFocus } from '../focus-manager/focus.action.svelte.js';
  import type { FocusItem, FocusLayer } from '../focus-manager/focus-types';
  import type { IconButtonSize } from '../types/icon-button';

  export let size: IconButtonSize = 'm';
  export let focusLayer: FocusLayer;
  export let focusName: FocusItem;

  export function focus() {
    button.focus();
  }

  let button: HTMLElement;
</script>

<button
  type="button"
  {...$$restProps}
  class="icon-button {$$props.class ?? ''}"
  class:size-large={size === 'l'}
  class:size-extra-large={size === 'xl'}
  data-focusName={focusName}
  data-focusLayer={focusLayer}
  bind:this={button}
  use:registerFocus={{ focusName, focusLayer }}
  on:click
>
  <slot />
</button>

<style module>
  .icon-button {
    color: #f0f0f0;
    color: var(--icon-color);
    background: transparent;
    background: var(--icon-background-color);
    border: none;
    border-radius: var(--icon-border-radius, 0);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 44px;
    height: 44px;
    padding: 4px;
    cursor: pointer;
  }

  @media (prefers-reduced-motion: no-preference) {
    .icon-button {
      transition: 0.3s ease-in-out;
      transition-property: background-color, color;
    }
  }

  .icon-button:focus {
    outline: 2px solid #ffffff; /* Fallback for older browsers */
    outline: var(--outline);
  }

  @supports selector(:focus-visible) {
    /* Don't show the outline on focus... */
    .icon-button:focus {
      outline: none;
    }

    /* ...but show it on focus-visible */
    .icon-button:focus-visible {
      outline: 2px solid #ffffff; /* Fallback for older browsers */
      outline: var(--outline);
    }
  }

  @media (prefers-contrast: more) {
    /* increase the contrast on focus (apply hover styles) */
    .icon-button:focus-visible {
      color: #f0f0f0;
      color: var(--icon-hover-color);
      background-color: #000000;
      background-color: var(--icon-hover-background-color);
    }

    .icon-button:active {
      outline: var(--icon-contrast-outline);
    }
  }

  @media (hover: hover) {
    .icon-button:enabled:hover {
      color: #f0f0f0;
      color: var(--icon-hover-color);
      background-color: #000000;
      background-color: var(--icon-hover-background-color);
    }

    @media (prefers-contrast: more) {
      .icon-button:enabled:hover {
        outline: var(--icon-contrast-outline);
      }
    }
  }

  .icon-button:active {
    color: #f0f0f0;
    color: var(--icon-hover-color);
    background-color: #000000;
    background-color: var(--icon-hover-background-color);
  }

  .icon-button:disabled {
    color: #424447; /* Fallback for older browsers */
    color: var(--icon-disabled-color);
    background-color: transparent;
    background-color: var(--icon-disabled-background-color);
    cursor: not-allowed;
  }

  .size-large :global(svg),
  .size-extra-large :global(svg) {
    width: 100%;
    height: 100%;
  }

  .size-large {
    width: 44px; /* Fallback for older browsers */
    height: 44px;
    width: clamp(44px, 3cqw + 2em, 64px);
    height: clamp(44px, 3cqw + 2em, 64px);
  }

  .size-extra-large {
    width: 64px; /* Fallback for older browsers */
    height: 64px;
    width: clamp(64px, 12cqw + 1em, 140px);
    height: clamp(64px, 12cqw + 1em, 140px);
  }
</style>
